import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { AppActions } from 'projects/semeris/src/store/app/app.actions';
import { RootState } from 'projects/semeris/src/store/root-state.model';
import { merge, Observable, pipe } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { WebSocketService } from './web-socket.service';
import { WARNING_NOTIFICATION_CONFIGURATION } from '../../constants/notification-configurations';
import { WebSocketEntities } from '../../models/web-socket/web-socket.models';

export interface PushNotification {
  message: string;
  isSuccess: boolean;
}

export type PushNotificationRecord = Record<string, PushNotification>;

export function createWebSocketEffect(
  store: Store<RootState>,
  actions$: Actions,
  webSocketService: WebSocketService,
  entity: WebSocketEntities,
  callback: (state: RootState) => Action,
  withId = false,
  ignoreTabId = false
): Observable<Action> {
  return createEffect(() =>
    actions$.pipe(
      ofType(AppActions.WebSocketSubscribe),
      webSocketSubscriptionPipe(
        store,
        actions$,
        webSocketService,
        entity,
        callback,
        withId,
        ignoreTabId
      )
    )
  );
}

export function createPersonalWebSocketEffect(
  store: Store<RootState>,
  actions$: Actions,
  webSocketService: WebSocketService,
  entity: WebSocketEntities,
  callback: (state: RootState) => Action,
  withId = false
): Observable<Action> {
  return createEffect(() =>
    actions$.pipe(
      ofType(AppActions.WebSocketSubscribe),
      personalWebSocketSubscriptionPipe(store, actions$, webSocketService, entity, callback, withId)
    )
  );
}

export function createWebSocketWithWarningEffect(
  store: Store<RootState>,
  actions$: Actions,
  webSocketService: WebSocketService,
  entity: WebSocketEntities,
  callback: (state: RootState) => Action | void,
  actionId: string
): Observable<Action> {
  return createEffect(() =>
    merge(
      actions$.pipe(
        ofType(AppActions.WebSocketSubscribe),
        webSocketSubscriptionPipe(
          store,
          actions$,
          webSocketService,
          entity,
          () =>
            AppActions.PushNotification({
              message: `Someone edited this page at the same time you did. Please reload the page and make your changes again.`,
              action: 'Reload',
              config: {
                ...WARNING_NOTIFICATION_CONFIGURATION,
                duration: undefined,
              },
              closable: true,
              id: actionId,
            }),
          true
        )
      ),
      actions$.pipe(
        ofType(AppActions.PushNotificationAction),
        filter((action) => action.id === actionId),
        withLatestFrom(store),
        map(([, store]) => callback(store)),
        map((result) => (result ? result : undefined)),
        filter((result) => !!result)
      )
    )
  );
}

function webSocketSubscriptionPipe(
  store: Store<RootState>,
  actions$: Actions,
  webSocketService: WebSocketService,
  entity: WebSocketEntities,
  callback: (state: RootState) => Action,
  withId: boolean,
  ignoreTabId = false
) {
  return pipe(
    map((action) => action as ReturnType<typeof AppActions.WebSocketUnsubscribe>),
    filter((action) => action.topic === entity && (withId ? !!action.id : !action.id)),
    switchMap((action) => {
      return webSocketService.subscribe(action.topic, action.id, { ignoreTabId }).pipe(
        takeUntil(
          actions$.pipe(
            ofType(AppActions.WebSocketUnsubscribe),
            filter((action) => action.topic === entity),
            tap(() => {
              webSocketService.unsubscribe(action.topic);
            })
          )
        ),
        withLatestFrom(store),
        map(([, store]) => callback(store))
      );
    })
  );
}

function personalWebSocketSubscriptionPipe(
  store: Store<RootState>,
  actions$: Actions,
  webSocketService: WebSocketService,
  entity: WebSocketEntities,
  callback: (state: RootState) => Action,
  withId: boolean
) {
  return pipe(
    map((action) => action as ReturnType<typeof AppActions.WebSocketUnsubscribe>),
    filter((action) => action.topic === entity && (withId ? !!action.id : !action.id)),
    switchMap((action) => {
      return webSocketService.subscribeToPersonalChannel(action.topic, action.id).pipe(
        takeUntil(
          actions$.pipe(
            ofType(AppActions.WebSocketUnsubscribe),
            filter((action) => action.topic === entity)
          )
        ),
        withLatestFrom(store),
        map(([, store]) => callback(store))
      );
    })
  );
}
